// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  src/features/garantias/hooks/useGarantia.ts
// ════════════════════════════════════════════════════════════════════════════

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { garantiaService } from '../services/garantiaService';
import { toast } from 'sonner';
import { getApiErrorMessage } from '@/lib/apiClient';
import type { ListaFiltros } from '@/features/operacional/services/listaFiltros';
import type { CriarGarantiaRequest, RegistrarOcorrenciaRequest, ResolverOcorrenciaRequest } from '../types';

type GarantiaFiltros = Omit<ListaFiltros, 'status'>;

const KEYS = {
  all:    ['garantias'] as const,
  lista:  (p: number, t: number, s: string, c: number, f: GarantiaFiltros) => ['garantias', 'lista', p, t, s, c, f] as const,
  detalhe: (id: number) => ['garantias', id] as const,
};

interface UseGarantiaParams {
  pagina?: number;
  tamanhoPagina?: number;
  status?: string;
  clienteId?: number;
  /** Busca, período e ordem — aplicados no servidor. */
  filtros?: GarantiaFiltros;
}

export const useGarantia = ({
  pagina = 1,
  tamanhoPagina = 20,
  status = 'todos',
  clienteId = 0,
  filtros = {},
}: UseGarantiaParams = {}) => {
  const qc = useQueryClient();

  const { data: paginado, isLoading, isFetching } = useQuery({
    queryKey: KEYS.lista(pagina, tamanhoPagina, status, clienteId, filtros),
    queryFn:  () => garantiaService.getAll(pagina, tamanhoPagina, status, clienteId, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

  const garantias   = paginado?.itens       ?? [];
  const total       = paginado?.total        ?? 0;
  const totalPaginas = paginado?.totalPaginas ?? 1;
  const resumo       = paginado?.resumo;

  const createMutation = useMutation({
    mutationFn: (req: CriarGarantiaRequest) => garantiaService.create(req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Garantia registrada com sucesso');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const encerrarMutation = useMutation({
    mutationFn: (id: number) => garantiaService.encerrar(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Garantia encerrada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => garantiaService.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.all });
      toast.success('Garantia excluída com sucesso');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    garantias, total, totalPaginas, resumo, isLoading, isFetching,
    create:       createMutation.mutateAsync,
    encerrar:     encerrarMutation.mutate,
    deleteGarantia: deleteMutation.mutate,
    isCreating:   createMutation.isPending,
  };
};

export const useGarantiaDetalhe = (id: number) => {
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: KEYS.detalhe(id),
    queryFn:  () => garantiaService.getById(id),
    enabled:  id > 0,
  });

  const ocorrenciaMutation = useMutation({
    mutationFn: (req: RegistrarOcorrenciaRequest) => garantiaService.registrarOcorrencia(id, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.detalhe(id) });
      toast.success('Ocorrência registrada');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  const resolverMutation = useMutation({
    mutationFn: ({ oid, req }: { oid: number; req: ResolverOcorrenciaRequest }) =>
      garantiaService.resolverOcorrencia(id, oid, req),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEYS.detalhe(id) });
      toast.success('Ocorrência resolvida');
    },
    onError: (e) => toast.error(getApiErrorMessage(e)),
  });

  return {
    ...query,
    registrarOcorrencia: ocorrenciaMutation.mutateAsync,
    resolverOcorrencia:  resolverMutation.mutateAsync,
    isRegistrando:       ocorrenciaMutation.isPending,
  };
};
